import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useTranslation } from 'next-i18next';
import { useCallback, useMemo, useState, useEffect } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';

import AddDevice from './AddDevice';
import AddDeviceSuccess from './AddDeviceSuccess';
import DeviceInstructions from './DeviceInstructions';

export type AddDeviceStage = 'addDevice' | 'instructions' | 'success';

export interface AddDeviceModalState {
  stage: AddDeviceStage;
}

export interface AddDeviceModalStageProps {
  open: boolean;
  state: AddDeviceModalState;
  groupId: number | null;
  setState: Dispatch<SetStateAction<AddDeviceModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
}

const StageComponents: Record<AddDeviceStage, FC<AddDeviceModalStageProps>> = {
  addDevice: AddDevice,
  instructions: DeviceInstructions,
  success: AddDeviceSuccess,
};

export interface AddDeviceModalProps extends DefaultPopupProps {
  groupId?: number;
}

const AddDeviceModal: FC<AddDeviceModalProps> = ({
  open,
  onClose,
  groupId,
}) => {
  const { t } = useTranslation();

  const [state, setState] = useState<AddDeviceModalState>({
    stage: 'addDevice',
  });

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev }));
    }
  }, [open]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    if (state.stage === 'addDevice') {
      setState(prev => ({ ...prev }));

      onClose();
    } else {
      setState(prev => ({ ...prev, stage: 'addDevice' }));
    }
  }, [onClose, state]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage !== 'addDevice' ? <ArrowBackRoundedIcon /> : undefined
      }
      header={
        state.stage !== 'addDevice' && state.stage !== 'success'
          ? t('common:add_new_device')
          : undefined
      }
      hideCloseButton={state.stage === 'success'}
    >
      <StageComponent
        open={open}
        setState={setState}
        state={state}
        groupId={groupId ?? null}
        onClose={onClose}
        onCloseProxy={onCloseProxy}
      />
    </Modal>
  );
};

export default AddDeviceModal;
